import { type ClassValue, clsx } from "clsx";
import { readableColor, shade, tint } from "polished";
import { type CSSProperties, type FC, useRef } from "react";
import { mergeProps, useFocusRing } from "react-aria";
import { AnimatedTooltip } from "widgets";
import { useTooltip } from "widgets/hooks";

import { SmartLink } from "@/ui-modules/core/SmartLink.js";

import { getCssColor } from "../../../app-utils/color.js";
import type { Project } from "../../../graphql.js";
import type { ProjectBasisFragment, ProjectColoredWidgetFragment } from "../graphql/projects-queries.gql.ts";
import style from "../styles/ProjectColoredWidgets.module.scss";

// --- Link ---
interface RouterColoredProjectProps {
  project: ProjectColoredWidgetFragment | null | undefined;
  alignment?: "right" | "left";
  className?: ClassValue;
  textClassName?: ClassValue;
  forceExternal?: boolean;
  noLink?: boolean;
  style?: CSSProperties;
}

interface BadgeProps {
  color: Project["color"];
  className?: ClassValue;
}

export const ProjectColoredLink: FC<RouterColoredProjectProps> = (props) => {
  if (!props.project) {
    return null;
  }

  const align = props.alignment || "left";
  const nameElement = <span className={style.text}>{props.project.name}</span>;

  return (
    <div
      className={clsx(style.container, { [style.container_right]: align === "right" }, props.className)}
      style={props.style}
    >
      {align === "left" && <ProjectMiniBadge color={props.project.color} className={style.mini_badge_left} />}
      {props.noLink ? (
        nameElement
      ) : (
        <SmartLink
          to={`/project/${props.project.id}`}
          forceExternal={props.forceExternal}
          className={clsx(style.default_link, props.textClassName)}
          tooltip={props.project.name}
        >
          {nameElement}
        </SmartLink>
      )}
      {align === "right" && <ProjectMiniBadge color={props.project.color} className={style.mini_badge_right} />}
    </div>
  );
};

export const ProjectMiniBadge: FC<BadgeProps> = ({ color, className }) => {
  const hexColor = getCssColor(color);
  return (
    <span style={{ backgroundColor: hexColor }} className={clsx(style.mini_badge, className)} aria-hidden="true" />
  );
};

// --- Badge ---
interface ProjectColoredBadgeProps {
  project?: ProjectBasisFragment | null | undefined;
  size?: "normal" | "small" | "menu"; // TODO Add tiny to replace ProjectColoredLink
  noLink?: boolean;
}

export const ProjectColoredBadge: FC<ProjectColoredBadgeProps> = (props) => {
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, { closeDelay: 0 });
  const { focusProps, isFocusVisible } = useFocusRing();

  const bgColor = getCssColor(props.project?.color);
  const txtColor = readableColor(bgColor, shade(0.6, bgColor), tint(0.6, bgColor), false);

  if (!props.project) return null;

  return (
    <>
      <SmartLink
        to={props.noLink !== true ? `/project/${props.project.id}` : undefined}
        className={clsx(style.badge, {
          [style.size_normal]: props.size == undefined || props.size === "normal",
          [style.size_small]: props.size === "small",
          [style.size_menu]: props.size === "menu",
          [style.badge_focused]: isFocusVisible,
        })}
        style={{ backgroundColor: bgColor, color: txtColor }}
        {...mergeProps(triggerProps, focusProps)}
        ref={triggerRef}
      >
        <span className={style.badge_text}>{props.project.name?.slice(0, 2).toUpperCase()}</span>
      </SmartLink>
      <AnimatedTooltip
        title={props.project.name}
        domProps={tooltipProps}
        ref={tooltipRef}
        targetRef={triggerRef}
        state={state}
        offset={4}
        nonInteractive
      >
        {props.project?.description}
      </AnimatedTooltip>
    </>
  );
};
